import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Button, Text } from '@chakra-ui/react';

import FormInput from '../../components/form/FormInput';
import LogoBox from '../../components/Header/LogoBox';
import loginBackground from '../../loginBackground.png';
import { createErrorToast, createSuccessToast } from '../../redux/modules/app';
import {
  selectInvalidUser,
  selectPasswordUser,
  validateUserToCreatePassword
} from '../../redux/modules/createPassword';
import { useResetPassword } from '../../services/user-service';
import boxStyles from '../../styles/boxStyles';
import colors from '../../styles/theme/foundation/colors';

const ResetPasswordPage = () => {
  const dispatch = useDispatch();
  const { userEmail, passwordKey } = useParams();
  const invalidUser = useSelector(selectInvalidUser);
  const user = useSelector(selectPasswordUser);
  const baseUrl = window.location.origin.toString();
  const history = useHistory();

  const { mutateAsync } = useResetPassword();

  useEffect(() => {
    dispatch(validateUserToCreatePassword(userEmail, passwordKey));
  }, [dispatch, passwordKey, userEmail]);

  const useFormMethods = useForm();
  const { reset, handleSubmit } = useFormMethods;

  const onSubmit = async data => {
    if (data.password !== data.password_2) {
      dispatch(createErrorToast({ title: 'Passwords do not match' }));
    }

    const userId = user?.userId;

    const response = await mutateAsync({ userId, password: data.password, key: passwordKey });

    if (response) {
      dispatch(createSuccessToast({
        title: 'Password reset successfully!',
        description: 'Taking you to the login page now.!'
      }));

      setTimeout(() => {
        window.location = `${baseUrl}/login`;
      }, 2000);

      reset();
    } else {
      dispatch(createErrorToast({
        title: 'Uh oh. Something went wrong',
        description: 'Please try again by resetting your browser or contact support at support@pinsadvantage.com'
      }));
    }
  };

  return (
    <Box
      backgroundImage={loginBackground}
      backgroundPosition="center"
      backgroundSize="cover"
      h="100vh"
      position="relative"
      w="100vw"
    >
      <Box
        h="40px"
        left={6}
        position="absolute"
        top={6}
      >
        <LogoBox to="/" />
      </Box>
      <Box
        alignItems="center"
        display="flex"
        h="100%"
        justifyContent="center"
        p={6}
      >
        <Box
          bgColor={colors.white}
          borderRadius="sm"
          boxShadow={boxStyles.boxShadow}
          mx="auto"
          my="auto"
          p={8}
          w="450px"
        >
          <Text
            mb={4}
            textAlign="center"
            textStyle="displayXs"
          >
            Reset Your Password
          </Text>
          {invalidUser
            ? (
              <>
                <Text>Oops!  Looks like either this link has expired or your email is invalid.</Text>
                <p>
                  {'Please contact your admin or '}
                  <a
                    href="mailto:support@pinsadvantage.com"
                    style={{ textDecoration: 'underline' }}
                  >
                    reach out to support.
                  </a>
                </p>

                <Button
                  my={4}
                  variant="secondary"
                  w="100%"
                  onClick={() => history.push('/login')}
                >
                  Go to Log In Page
                </Button>
              </>
            )
            : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box mb={2}>
                  <FormInput
                    inputName="password"
                    labelName="New Password"
                    placeholder="New Password"
                    type="password"
                    useFormMethods={useFormMethods}
                    validationRules={{ required: 'Please provide a password.' }}
                  />

                  <FormInput
                    inputName="password_2"
                    labelName="Confirm Password"
                    placeholder="New Password"
                    type="password"
                    useFormMethods={useFormMethods}
                    validationRules={{ required: 'Please confirm your password.' }}
                  />
                </Box>

                <Button mb={4} type="submit" w="100%">Reset Password</Button>
              </form>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
